import React, { useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { GitBranch, Upload, Layers, Sparkles, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Workspace Dock - Left Panel (Authoring Context)
 */
const WorkspaceDock = ({ 
  machines, 
  selectedMachine, 
  onSelectMachine,
  onNewMachine,
  onImportMachine,
  onOpenTemplates,
  onOpenAIGenerator,
  onDeleteMachine
}) => {
  const [cfspScores, setCfspScores] = useState({});

  // Fetch CFSP-1 scores for all machines
  const loadScores = useCallback(async () => {
    if (machines.length === 0) return;
    try {
      const resp = await fetch(`${API_URL}/api/machines/cfsp1/scores`);
      if (resp.ok) {
        const data = await resp.json();
        setCfspScores(data);
      }
    } catch {
      // Silent fail - scores are supplementary
    }
  }, [machines.length]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'bg-green-500';
    if (score >= 0.6) return 'bg-yellow-500';
    if (score >= 0.4) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const handleDelete = (e, machineId) => {
    e.stopPropagation();
    if (onDeleteMachine) onDeleteMachine(machineId);
  };

  return (
    <div className="h-full flex flex-col" data-testid="workspace-dock">
      {/* Create / Import / Templates Buttons */}
      <div className="p-3 border-b border-white/10 flex-shrink-0 space-y-2">
        <div className="flex space-x-2">
          <button
            onClick={onNewMachine}
            className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded flex items-center justify-center transition-colors"
            data-testid="new-machine-button"
          >
            <span className="text-lg mr-1">+</span>
            New
          </button>
          <button
            onClick={onOpenAIGenerator}
            className="flex-1 h-9 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded flex items-center justify-center transition-colors"
            data-testid="ai-generate-button"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            AI
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onOpenTemplates}
            className="flex-1 h-8 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-xs font-medium rounded flex items-center justify-center transition-colors border border-purple-500/30"
            data-testid="templates-button"
          >
            <Layers className="w-3.5 h-3.5 mr-1.5" />
            Templates
          </button>
          <button
            onClick={onImportMachine}
            className="flex-1 h-8 bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium rounded flex items-center justify-center transition-colors border border-white/10"
            data-testid="import-machine-button"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Import
          </button>
        </div>
      </div>

      {/* Machine List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          <div className="text-[10px] font-mono text-gray-500 px-2 py-1 uppercase tracking-wider flex items-center justify-between">
            <span>Machines</span>
            <span className="text-gray-600">{machines.length}</span>
          </div>
          {machines.map((machine) => {
            const isSelected = selectedMachine === machine.machine_id;
            const score = cfspScores[machine.machine_id];
            const stateCount = machine.machine_ir?.states
              ? Object.keys(machine.machine_ir.states).length
              : 0;
            
            return (
              <button
                key={machine.machine_id}
                onClick={() => onSelectMachine(machine.machine_id)}
                className={cn(
                  "w-full text-left p-2 rounded transition-all text-sm group",
                  isSelected
                    ? "bg-blue-500/20 border border-blue-500/50"
                    : "hover:bg-white/5 border border-transparent"
                )}
                data-testid={`machine-${machine.machine_id}`}
              >
                <div className="flex items-start space-x-2">
                  {/* CFSP-1 Score Indicator */}
                  <div className="flex-shrink-0 mt-1.5" title={score ? `CFSP-1: ${Math.round(score.control_score * 100)}%` : 'Analyzing...'}>
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      score ? getScoreColor(score.control_score) : "bg-gray-600 animate-pulse"
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-sm truncate">{machine.name}</div>
                      {/* Delete button - visible on hover */}
                      <button
                        onClick={(e) => handleDelete(e, machine.machine_id)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-500/20 rounded transition-all"
                        title="Delete machine"
                        data-testid={`delete-machine-${machine.machine_id}`}
                      >
                        <Trash2 className="w-3 h-3 text-gray-500 hover:text-red-400" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2 mt-0.5">
                      <span className="text-[10px] font-mono text-gray-500 truncate">
                        {machine.version_hash.substring(0, 10)}
                      </span>
                      {stateCount > 0 && (
                        <Badge variant="outline" className="text-[9px] h-3.5 px-1 text-gray-500 border-white/10">
                          {stateCount}s
                        </Badge>
                      )}
                      {score && score.critical_count > 0 && (
                        <Badge variant="destructive" className="text-[9px] h-3.5 px-1">
                          {score.critical_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {machines.length === 0 && (
            <div className="text-center py-8 px-3">
              <img src="/firmastate-icon.png" alt="FIRMASTATE" className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-xs text-gray-500">No machines yet</p>
              <p className="text-[10px] text-gray-600 mt-1">
                Create, import, or use a template
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Version / Branch Placeholder */}
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
          <GitBranch className="w-3 h-3 mr-1" />
          Versions
        </div>
        <div className="text-[10px] text-gray-600">
          {machines.length > 0 ? `${machines.length} machine${machines.length === 1 ? '' : 's'} tracked` : 'No machines to track'}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceDock;
